import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import useToast from '../hooks/useToast';
import ToastContainer from './ToastContainer';
import api from '../services/api';

const Header = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);
  const navigate = useNavigate();
  const [searchResults, setSearchResults] = useState([]);
  const location = useLocation();
  const { toasts, removeToast, showSuccess } = useToast();
  const apiUrl = process.env.REACT_APP_API_URL;
  // User state
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Kullanıcı bilgilerini çek
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        setLoading(true);
                const response = await api.getProfile();
                if (response.success) {
                  setUser(response.user);
                }
      } catch (error) {
        console.error('Kullanıcı bilgileri alınamadı:', error);
        // Token geçersizse login sayfasına yönlendir
        if (error.message.includes('401')) {
          localStorage.removeItem('userToken');
          localStorage.removeItem('isAuthenticated');
          navigate('/giris');
        }
      } finally {
        setLoading(false);
      }
    };

    // Sadece token varsa kullanıcı bilgilerini çek
    if (localStorage.getItem('userToken')) {
      fetchUserProfile();
    } else {
      setLoading(false);
    }
  }, [navigate]);

  // Get page title based on current route
  const getPageTitle = () => {
    const routeTitles = {
      '/': 'Anasayfa',
      '/anasayfa': 'Anasayfa',
      '/duyurular': 'Haberler ve Duyurular',
      '/personel-rehberi': 'Personel Rehberi',
      '/dokuman-merkezi': 'Doküman Merkezi',
      '/insan-kaynaklari': 'İnsan Kaynakları',
      '/projeler': 'Proje Çalışma Alanları',
      '/isin-uzmanina-sor': 'İşin Uzmanına Sor',
      '/kolin-dunyasi-haritasi': 'Kolin Dünyası Haritası',
      '/basari-hikayeleri': 'Başarı Hikayeleri',
      '/profil': 'Kullanıcı Profili',
      '/etkinlik-takvimi': 'Etkinlik Takvimi',
      '/inovasyon-oneri': 'İnovasyon ve Öneri Kutusu',
      '/kolin-akademi': 'Kolin Akademi'
    };
    return routeTitles[location.pathname] || 'KolinNet';
  };

  const handleSearchInput = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    if (query.trim()) {
      const allResults = [
        { title: 'Personel Rehberi', url: '/personel-rehberi', type: 'Sayfa', keywords: ['personel', 'çalışan', 'rehber', 'kişi'] },
        { title: 'Duyurular', url: '/duyurular', type: 'Sayfa', keywords: ['duyuru', 'açıklama', 'bilgi'] },
        { title: 'İnsan Kaynakları', url: '/insan-kaynaklari', type: 'Sayfa', keywords: ['ik', 'izin', 'masraf', 'form'] },
        { title: 'İdari İşler', url: '/idari-isler', type: 'Sayfa', keywords: ['talep', 'kırtasiye', 'telefon', 'bilgisayar'] },
        { title: 'Zimmet Yönetimi', url: '/zimmet-yonetimi', type: 'Sayfa', keywords: ['zimmet', 'envanter', 'malzeme', 'takip'] },
        { title: 'Doküman Merkezi', url: '/dokuman-merkezi', type: 'Sayfa', keywords: ['doküman', 'belge', 'dosya', 'pdf'] },
        { title: 'Kullanıcı Profili', url: '/profil', type: 'Sayfa', keywords: ['profil', 'hesap', 'bilgiler', 'ayarlar'] },
        { title: 'Etkinlik Takvimi', url: '/etkinlik-takvimi', type: 'Sayfa', keywords: ['etkinlik', 'takvim', 'event'] },
        { title: 'Öneri ve Talep Kutusu', url: '/inovasyon-oneri', type: 'Sayfa', keywords: ['öneri', 'talep', 'fikir'] },
        { title: 'Koloğlu Akademi', url: 'https://akademi.koloğlu.com.tr', type: 'Dış Link', keywords: ['akademi', 'eğitim', 'kurs'] }
      ];
      
      const filteredResults = allResults.filter(item => 
        item.title.toLowerCase().includes(query.toLowerCase()) ||
        item.keywords.some(keyword => keyword.toLowerCase().includes(query.toLowerCase()))
      );
      
      setSearchResults(filteredResults);
      setShowSearchResults(true);
    } else {
      setShowSearchResults(false);
      setSearchResults([]);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim() && searchResults.length > 0) {
      window.location.href = searchResults[0].url;
    }
  };

  const toggleMobileSidebar = () => {
    const body = document.getElementsByTagName('body')[0];
    const overlay = document.querySelector('.overlay');
    body.classList.toggle('active');
    overlay.classList.toggle('active');
  };

  return (
    <div className="main-header">
      <div className="d-flex">
        <div className="mobile-toggle" id="mobile-toggle" onClick={toggleMobileSidebar}>
          <i className='bx bx-menu'></i>
        </div>
        <div className="main-title">
          {getPageTitle()}
        </div>
      </div>

      <div className="d-flex align-items-center">
        {/* Search Form */}
        <form className="app-search d-none d-lg-block" onSubmit={handleSearch}>
          <div className="position-relative">
            <input 
              type="text" 
              className="form-control" 
              placeholder="Sayfalar, belgeler, kişiler ara..."
              value={searchQuery}
              onChange={handleSearchInput}
              onFocus={() => searchQuery && setShowSearchResults(true)}
              onBlur={() => setTimeout(() => setShowSearchResults(false), 200)}
            />
            <span className="bx bx-search-alt" onClick={handleSearch}></span>

            {/* Search Results Dropdown */}
            {showSearchResults && (
              <div className="search-results-dropdown">
                {searchResults.length > 0 ? (
                  searchResults.map((result, index) => (
                    <div 
                      key={index} 
                      className="search-result-item"
                      onClick={() => {
                        window.location.href = result.url;
                        setShowSearchResults(false);
                      }}
                    >
                      <i className="bx bx-file text-muted me-2"></i>
                      <div>
                        <div className="result-title">{result.title}</div>
                        <small className="text-muted">{result.type}</small>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="search-result-item no-results">
                    <i className="bx bx-search text-muted me-2"></i>
                    <div>
                      <div className="result-title">Sonuç bulunamadı</div>
                      <small className="text-muted">"{searchQuery}" için sonuç yok</small>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </form>

        {/* Mobile Search */}
        <div className="dropdown d-inline-block d-lg-none ms-2">
          <button 
            type="button" 
            className="btn header-item noti-icon waves-effect" 
            id="page-header-search-dropdown" 
            data-bs-toggle="dropdown"
          >
            <i className='bx bx-search-alt'></i>
          </button>
          <div className="dropdown-menu dropdown-menu-lg dropdown-menu-end p-0">
            <form className="p-3" onSubmit={handleSearch}>
              <div className="form-group m-0">
                <div className="input-group">
                  <input 
                    type="text" 
                    className="form-control" 
                    placeholder="Sayfalar, belgeler, kişiler ara..."
                    value={searchQuery}
                    onChange={handleSearchInput}
                  />
                  <div className="input-group-append">
                    <button className="btn btn-primary h-100" type="submit">
                      <i className='bx bx-search-alt'></i>
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>

        {/* Notifications */}
        <div className="dropdown d-inline-block">
          <button 
            type="button" 
            className="btn header-item noti-icon waves-effect" 
            data-bs-toggle="dropdown"
            id="notificationDropdown"
          >
            <i className='bx bx-bell'></i>
            <span className="badge bg-danger rounded-pill">3</span>
          </button>
          <div className="dropdown-menu dropdown-menu-end" aria-labelledby="notificationDropdown">
            <div className="dropdown-header">
              <h6 className="mb-0">Bildirimler (3)</h6>
            </div>
            <div className="dropdown-item d-flex align-items-center">
              <i className="bx bx-info-circle text-primary me-3"></i>
              <div>
                <div className="fw-medium">Yeni proje ataması yapıldı</div>
                <small className="text-muted">2 saat önce</small>
              </div>
            </div>
            <div className="dropdown-item d-flex align-items-center">
              <i className="bx bx-message text-success me-3"></i>
              <div>
                <div className="fw-medium">İK'dan yeni mesaj var</div>
                <small className="text-muted">5 saat önce</small>
              </div>
            </div>
            <div className="dropdown-item d-flex align-items-center">
              <i className="bx bx-calendar text-warning me-3"></i>
              <div>
                <div className="fw-medium">Toplantı hatırlatması</div>
                <small className="text-muted">1 gün önce</small>
              </div>
            </div>
            <div className="dropdown-divider"></div>
            <div className="dropdown-item text-center text-primary">
              Tüm bildirimleri gör
            </div>
          </div>
        </div>

        {/* User Profile Dropdown */}
        <div className="dropdown d-inline-block">
          <button 
            type="button" 
            className="btn header-item waves-effect d-flex align-items-center" 
            id="page-header-user-dropdown" 
            data-bs-toggle="dropdown"
            disabled={loading}
          >
            {loading ? (
              <div className="spinner-border spinner-border-sm me-2" role="status">
                <span className="visually-hidden">Yükleniyor...</span>
              </div>
                    ) : user ? (
                      <>
                        {user.avatar && user.avatar.filePath ? (
                          <img 
                            src={`${apiUrl}/uploads/avatars/${user.avatar.filename}`}
                            alt="Profil" 
                            className="rounded-circle header-profile-user me-2"
                            style={{ 
                              width: '32px', 
                              height: '32px',
                              objectFit: 'cover'
                            }}
                          />
                        ) : (
                          <div 
                            className="rounded-circle header-profile-user me-2 d-flex align-items-center justify-content-center text-white fw-bold"
                            style={{ 
                              width: '32px', 
                              height: '32px',
                              backgroundColor: '#3C21F7',
                              fontSize: '12px'
                            }}
                          >
                            {user.firstName?.charAt(0)}{user.lastName?.charAt(0)}
                          </div>
                        )}
                <span className="d-none d-md-inline-block">
                  <span className="fw-medium">{user.firstName} {user.lastName}</span>
                </span>
              </>
            ) : (
              <div 
                className="rounded-circle header-profile-user me-2 d-flex align-items-center justify-content-center text-white fw-bold"
                style={{ 
                  width: '32px', 
                  height: '32px',
                  backgroundColor: '#6c757d',
                  fontSize: '12px'
                }}
              >
                ?
              </div>
            )}
            <i className='bx bx-chevron-down ms-1'></i>
          </button>
          
          <div className="dropdown-menu dropdown-menu-end" aria-labelledby="page-header-user-dropdown">
            {user ? (
              <>
                <div className="dropdown-header">
                  <h6 className="mb-0">{user.firstName} {user.lastName}</h6>
                  <small className="text-muted">{user.email}</small>
                  <small className="text-muted d-block">{user.department} - {user.position}</small>
                </div>
              </>
            ) : (
              <div className="dropdown-header">
                <h6 className="mb-0">Kullanıcı Bilgisi Yüklenemedi</h6>
                <small className="text-muted">Lütfen sayfayı yenileyin</small>
              </div>
            )}
            <div className="dropdown-divider"></div>
            <a className="dropdown-item" href="/profil">
              <i className="bx bx-user me-2"></i> 
              <span>Profili Düzenle</span>
            </a>
            <div className="dropdown-item">
              <i className="bx bx-cog me-2"></i> 
              <span>Hesap Ayarları</span>
            </div>
            <div className="dropdown-item">
              <i className="bx bx-bell me-2"></i> 
              <span>Bildirim Ayarları</span>
            </div>
            <div className="dropdown-item">
              <i className="bx bx-help-circle me-2"></i> 
              <span>Yardım ve Destek</span>
            </div>
            <div className="dropdown-divider"></div>
            <button className="dropdown-item text-danger" onClick={() => {
              if (window.confirm('Çıkış yapmak istediğinizden emin misiniz?')) {
                // Authentication bilgilerini temizle
                localStorage.removeItem('isAuthenticated');
                localStorage.removeItem('userEmail');
                localStorage.removeItem('userToken');
                localStorage.removeItem('userData');
                localStorage.removeItem('loginTime');
                localStorage.removeItem('rememberMe');
                
                // User state'i temizle
                setUser(null);
                
                showSuccess('Güvenli çıkış yapıldı.');
                setTimeout(() => {
                  navigate('/giris');
                }, 1000);
              }
            }}>
              <i className="bx bx-power-off me-2 text-danger"></i> 
              <span>Çıkış Yap</span>
            </button>
          </div>
        </div>
      </div>
      
      {/* Toast Container */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
};

export default Header; 